/**
 * ArAggregationTag
 *
 * Names a totals-row aggregation, so an average, a min, a max and a count are
 * not indistinguishable from a sum. The tag follows the value it qualifies: the
 * eye lands on the number first, the word reads second. Consumed only by
 * `ArTableAggregations`, from every branch that renders an aggregated value —
 * the compound prefix/suffix branch and the `ArCellRouter` branch alike.
 *
 * `sum` is the unmarked default and renders nothing. `aggregationTagWords` is
 * exhaustive over `ArColumnAggregation`, so a sixth aggregation cannot compile
 * without a word here. The tag is a quiet marker, never a status: it carries no
 * colour, on screen or in a PDF export.
 */

import { FC, memo } from 'react';
import { Tag, theme } from 'antd';
import type { ArColumnAggregation } from '#types/System.Artifacts.types';

const aggregationTagWords: Record<ArColumnAggregation, string | null> = {
  sum: null,
  avg: 'avg',
  min: 'min',
  max: 'max',
  count: 'count',
};

type ArAggregationTagProps = {
  aggregation: ArColumnAggregation;
};

const ArAggregationTag: FC<ArAggregationTagProps> = ({ aggregation }) => {
  const { token } = theme.useToken();
  const word = aggregationTagWords[aggregation];

  if (word === null) return null;

  return (
    <Tag
      aria-label={`artable-aggregation-tag-${aggregation}`}
      style={{
        flexShrink: 0,
        fontSize: token.fontSizeSM,
        lineHeight: token.lineHeightSM,
        marginInlineStart: token.marginXXS,
        marginInlineEnd: 0,
        paddingInline: token.paddingXXS,
      }}
    >
      {word}
    </Tag>
  );
};

export default memo(ArAggregationTag);
